import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EventIcon from '@mui/icons-material/Event';
import HomeIcon from '@mui/icons-material/Home';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import PasswordIcon from '@mui/icons-material/Password';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
    Box,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { NavButton } from '../Components/NavButton';
import { Person } from '../Models/person';
import { logout } from '../Store/SignedInSlice';
import { SignOut } from '../firebase/Auth/SignOut';
import { getPerson } from '../firebase/Database/GetPerson';
import { getSignedInPersonId } from '../firebase/Database/GetSignedInPersonId';
import { auth } from '../firebase/firebase';

export const MainNavigation: React.FC = () => {
    const dispatch = useDispatch();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const navigate = useNavigate();
    const [currentUser, setCurrentUser] = useState<null | Person>(null);
    const [buttons, setButtons] = useState<
        {
            key: string;
            to: string;
            text: string;
            onClick?: () => void;
            icon: React.ReactNode;
        }[]
    >([]);

    const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleSignOut = useCallback(() => {
        SignOut();
        dispatch(logout());
    }, [dispatch]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                // There is a user logged in
                user.getIdTokenResult().then((idTokenResult) => {
                    if (idTokenResult.claims.admin) {
                        // This user is an admin
                        setCurrentUser({
                            email: '',
                            firstName: 'ADMIN',
                            lastName: '',
                            wantsToBeSeen: true,
                        });
                        setButtons([
                            {
                                key: 'home',
                                to: '/',
                                text: 'Domů',
                                icon: <HomeIcon />,
                            },
                            {
                                key: 'admin-sessions',
                                to: '/admin-sessions',
                                text: 'Lekce',
                                icon: <EventIcon />,
                            },
                            {
                                key: 'sign-up',
                                to: '/sign-up',
                                text: 'Vytvořit uživatele',
                                icon: <PersonAddIcon />,
                            },
                            {
                                key: 'users',
                                to: 'users',
                                text: 'Seznam uživatelů',
                                icon: <PeopleIcon />,
                            },
                            {
                                key: 'sign-out',
                                to: '/',
                                text: 'Odhlásit',
                                onClick: handleSignOut,
                                icon: <LogoutIcon />,
                            },
                        ]);
                    } else {
                        // This user is not an admin
                        const userId = getSignedInPersonId();
                        getPerson(userId).then((res) => {
                            setCurrentUser(res);
                        });
                        setButtons([
                            {
                                key: 'home',
                                to: '/',
                                text: 'Domů',
                                icon: <HomeIcon />,
                            },
                            {
                                key: 'sessions',
                                to: '/sessions',
                                text: 'Lekce',
                                icon: <EventIcon />,
                            },
                            {
                                key: 'password-update',
                                to: '/password-update',
                                text: 'Změnit heslo',
                                icon: <PasswordIcon />,
                            },
                            {
                                key: 'sign-out',
                                to: '/',
                                text: 'Odhlásit',
                                onClick: handleSignOut,
                                icon: <LogoutIcon />,
                            },
                        ]);
                    }
                });
            } else {
                // there is no user logged in now
                setCurrentUser(null);
                setButtons([
                    {
                        key: 'home',
                        to: '/',
                        text: 'Domů',
                        icon: <HomeIcon />,
                    },
                    {
                        key: 'sign-in',
                        to: '/sign-in',
                        text: 'Přihlásit',
                        icon: <LoginIcon />,
                    },
                ]);
            }
        });

        return () => unsubscribe();
    }, [handleSignOut]);

    return (
        <Box
            bgcolor="primary.main"
            sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: currentUser ? 'space-between' : 'flex-end',
                height: '100%',
                alignItems: 'center',
            }}
        >
            {currentUser && (
                <Box
                    sx={{
                        marginLeft: '20px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <AccountCircleIcon
                        sx={{
                            width: 32,
                            height: 32,
                            marginRight: '10px',
                        }}
                        color="secondary"
                    />
                    <Typography sx={{ fontWeight: 'bold' }}>
                        {currentUser.firstName} {currentUser.lastName}
                    </Typography>
                </Box>
            )}
            <Box sx={{ display: { xs: 'flex', lg: 'none' } }}>
                <IconButton
                    onClick={handleMenuClick}
                    size="large"
                    sx={{ marginRight: '20px' }}
                >
                    <MenuIcon sx={{ width: 32, height: 32 }} />
                </IconButton>
            </Box>
            <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
                {buttons.map((button, index) => (
                    <MenuItem
                        key={index}
                        onClick={() => {
                            navigate(button.to);
                            handleMenuClose();
                            button.key === 'sign-out' && handleSignOut();
                        }}
                    >
                        <ListItemIcon>{button.icon}</ListItemIcon>
                        {button.text}
                    </MenuItem>
                ))}
            </Menu>
            <Box
                sx={{
                    height: '100%',
                    display: { xs: 'none', lg: 'flex' },
                    flexDirection: 'row',
                    alignItems: 'center',
                }}
            >
                {buttons.map((button) => (
                    <NavButton
                        text={button.text}
                        to={button.to}
                        key={button.key}
                        onClick={button.onClick ? button.onClick : undefined}
                        icon={button.icon}
                    />
                ))}
            </Box>
        </Box>
    );
};
